import { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { fmtNum, fmtDateTime, MOVEMENT_TYPES } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { DataTable } from "../components/DataTable";
import { Modal, Field, inputCls, selectCls, btnPrimary, btnSecondary } from "../components/Modal";
import { toast } from "sonner";
import { ArrowLeftRight, PackagePlus, Plus } from "lucide-react";

export default function Stock() {
  const { hasPerm } = useAuth();
  const [tab, setTab] = useState("levels");
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [movements, setMovements] = useState([]);
  const [movPg, setMovPg] = useState({ total: 0, pages: 1 });
  const [movPage, setMovPage] = useState(1);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [whOpen, setWhOpen] = useState(false);
  const [adj, setAdj] = useState({ product_id: "", warehouse_id: "", type: "entry", qty: 1, reason: "" });
  const [tr, setTr] = useState({ product_id: "", from_warehouse_id: "", to_warehouse_id: "", qty: 1, reason: "" });
  const [whName, setWhName] = useState("");

  const load = () => {
    api.get("/products", { params: { limit: 2000 } }).then((r) => setProducts(r.data.items)).catch((e) => toast.error(errMsg(e)));
    api.get("/warehouses").then((r) => setWarehouses(r.data)).catch(() => {});
    loadMovements(movPage);
  };
  const loadMovements = (p = 1) => {
    if (hasPerm("stock.view"))
      api.get("/stock/movements", { params: { page: p, limit: 50 } })
        .then((r) => { setMovements(r.data.items); setMovPg({ total: r.data.total, pages: r.data.pages }); })
        .catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { loadMovements(movPage); }, [movPage]);

  const doAdjust = async () => {
    try {
      await api.post("/stock/adjust", { ...adj, qty: Number(adj.qty) });
      toast.success("Mouvement de stock enregistré");
      setAdjustOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const doTransfer = async () => {
    try {
      await api.post("/stock/transfer", { ...tr, qty: Number(tr.qty) });
      toast.success("Transfert effectué");
      setTransferOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addWarehouse = async () => {
    try {
      await api.post("/warehouses", { name: whName, type: "depot" });
      toast.success("Dépôt créé");
      setWhOpen(false); setWhName("");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const levelCols = [
    { key: "name", label: "Produit", render: (p) => <span className="font-semibold">{p.name}</span> },
    { key: "unit", label: "Unité" },
    ...warehouses.map((w) => ({
      key: `wh_${w.id}`, label: w.name, className: "text-right",
      render: (p) => <span className="font-money">{fmtNum((p.stock_by_warehouse || {})[w.id] || 0)}</span>,
    })),
    { key: "stock", label: "Total", className: "text-right", render: (p) => (
      <span className={`font-money font-bold ${p.stock <= 0 ? "text-rose-600" : p.alert_threshold > 0 && p.stock <= p.alert_threshold ? "text-amber-600" : ""}`}>{fmtNum(p.stock)}</span>) },
  ];

  const movCols = [
    { key: "created_at", label: "Date", render: (m) => fmtDateTime(m.created_at) },
    { key: "product_name", label: "Produit", render: (m) => <span className="font-semibold">{m.product_name}</span> },
    { key: "type", label: "Type", render: (m) => MOVEMENT_TYPES[m.type] || m.type },
    { key: "delta", label: "Quantité", className: "text-right", render: (m) => (
      <span className={`font-money font-bold ${m.delta >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{m.delta >= 0 ? "+" : ""}{fmtNum(m.delta)}</span>) },
    { key: "qty_before", label: "Avant", className: "text-right", render: (m) => <span className="font-money">{fmtNum(m.qty_before)}</span> },
    { key: "qty_after", label: "Après", className: "text-right", render: (m) => <span className="font-money">{fmtNum(m.qty_after)}</span> },
    { key: "reason", label: "Motif" },
    { key: "user_name", label: "Par" },
  ];

  return (
    <div className="space-y-4" data-testid="stock-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Gestion du Stock</h1>
        <div className="flex gap-2 flex-wrap">
          {hasPerm("warehouses.manage") && <button data-testid="add-warehouse-btn" onClick={() => setWhOpen(true)} className={btnSecondary}><Plus className="h-4 w-4" /> Dépôt</button>}
          {hasPerm("stock.transfer") && <button data-testid="transfer-btn" onClick={() => setTransferOpen(true)} className={btnSecondary}><ArrowLeftRight className="h-4 w-4" /> Transfert</button>}
          {hasPerm("stock.adjust") && <button data-testid="adjust-btn" onClick={() => setAdjustOpen(true)} className={btnPrimary}><PackagePlus className="h-4 w-4" /> Mouvement</button>}
        </div>
      </div>

      <div className="flex gap-2">
        {[["levels", "Niveaux de stock"], ["movements", "Historique des mouvements"]].map(([k, l]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 h-10 rounded-lg text-sm font-semibold transition-colors ${tab === k ? "bg-primary text-primary-foreground" : "bg-card border hover:bg-muted"}`}>{l}</button>
        ))}
      </div>

      {tab === "levels" && <DataTable testid="stock-levels" columns={levelCols} rows={products} searchKeys={["name", "sku", "category"]} />}
      {tab === "movements" && <DataTable testid="stock-movements" columns={movCols} rows={movements} serverSide
        serverTotal={movPg.total} serverPage={movPage} serverPages={movPg.pages} onPageChange={setMovPage} pageSize={50} />}

      <Modal open={adjustOpen} onClose={() => setAdjustOpen(false)} title="Mouvement de stock">
        <div className="space-y-3">
          <Field label="Produit">
            <select data-testid="adj-product" value={adj.product_id} onChange={(e) => setAdj({ ...adj, product_id: e.target.value })} className={selectCls}>
              <option value="">— Choisir —</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name} (stock: {fmtNum(p.stock)} {p.unit})</option>)}
            </select>
          </Field>
          <Field label="Dépôt">
            <select data-testid="adj-warehouse" value={adj.warehouse_id} onChange={(e) => setAdj({ ...adj, warehouse_id: e.target.value })} className={selectCls}>
              <option value="">— Choisir —</option>
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <select data-testid="adj-type" value={adj.type} onChange={(e) => setAdj({ ...adj, type: e.target.value })} className={selectCls}>
                <option value="entry">Entrée marchandise</option>
                <option value="exit">Sortie</option>
                <option value="return_customer">Retour client</option>
                <option value="return_supplier">Retour fournisseur</option>
                <option value="damage">Casse / endommagé</option>
                <option value="loss">Perte</option>
                <option value="adjustment">Ajustement (+/- signé)</option>
              </select>
            </Field>
            <Field label="Quantité"><input data-testid="adj-qty" type="number" step="any" value={adj.qty} onChange={(e) => setAdj({ ...adj, qty: e.target.value })} className={inputCls} /></Field>
          </div>
          <Field label="Motif"><input data-testid="adj-reason" value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })} className={inputCls} /></Field>
          <button data-testid="adj-submit" onClick={doAdjust} disabled={!adj.product_id || !adj.warehouse_id} className={`${btnPrimary} w-full`}>Valider le mouvement</button>
        </div>
      </Modal>

      <Modal open={transferOpen} onClose={() => setTransferOpen(false)} title="Transfert entre dépôts">
        <div className="space-y-3">
          <Field label="Produit">
            <select data-testid="tr-product" value={tr.product_id} onChange={(e) => setTr({ ...tr, product_id: e.target.value })} className={selectCls}>
              <option value="">— Choisir —</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="De">
              <select data-testid="tr-from" value={tr.from_warehouse_id} onChange={(e) => setTr({ ...tr, from_warehouse_id: e.target.value })} className={selectCls}>
                <option value="">—</option>
                {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </Field>
            <Field label="Vers">
              <select data-testid="tr-to" value={tr.to_warehouse_id} onChange={(e) => setTr({ ...tr, to_warehouse_id: e.target.value })} className={selectCls}>
                <option value="">—</option>
                {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Quantité"><input data-testid="tr-qty" type="number" step="any" value={tr.qty} onChange={(e) => setTr({ ...tr, qty: e.target.value })} className={inputCls} /></Field>
          <button data-testid="tr-submit" onClick={doTransfer} disabled={!tr.product_id || !tr.from_warehouse_id || !tr.to_warehouse_id} className={`${btnPrimary} w-full`}>Transférer</button>
        </div>
      </Modal>

      <Modal open={whOpen} onClose={() => setWhOpen(false)} title="Nouveau dépôt / magasin">
        <div className="space-y-3">
          <Field label="Nom"><input data-testid="wh-name" value={whName} onChange={(e) => setWhName(e.target.value)} className={inputCls} placeholder="Ex: Dépôt 2, Boutique Adjamé" /></Field>
          <button data-testid="wh-submit" onClick={addWarehouse} disabled={!whName} className={`${btnPrimary} w-full`}>Créer</button>
        </div>
      </Modal>
    </div>
  );
}
